import { apiSlice } from './apiSlice';
import { NotificationActions } from './notificationSlice';

// Helper function to process columnFilters for filter data queries
const processFiltersForQuery = (columnFilters = []) => {
    // Extract validation filter from columnFilters
    const validationFilterObj = columnFilters.find(f => f.id === 'validationFilter');

    const validationFilter = validationFilterObj?.value || 'all';

    // Get other filters (excluding validationFilter)
    const otherFilters = columnFilters.filter(f => f.id !== 'validationFilter' && f.id !== 'includePackItems');
    
    // Transform filter IDs for API compatibility - replace periods with underscores
    const transformedFilters = otherFilters.map(filter => ({
        ...filter,
        id: filter.id.replace(/\./g, '_')
    }));
    
    let allFilters = [...transformedFilters];
    if (validationFilter === 'validated') {
        allFilters.push({ id: 'isManuallyValidated', value: true });
        allFilters.push({ id: 'hasImages', value: true });
    } else if (validationFilter === 'unvalidated') {
        allFilters.push({ id: 'isManuallyValidated', value: false });
        allFilters.push({ id: 'hasImages', value: true });
    } else if (validationFilter === 'no-images') {
        allFilters.push({ id: 'hasImages', value: false });
    } else {
        allFilters.push({ id: 'hasImages', value: true });
    }
    
    return JSON.stringify(allFilters);
};

export const itemSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        updateCaseItem: builder.mutation({
            query: ({ caseId, item, openQuantity, holdQuantity }) => ({
                url: `/cases/${caseId}/items/${item.id}`,
                method: 'PATCH',
                body: {
                    open_quantity: openQuantity,
                    hold_quantity: holdQuantity
                } 
            }),
            async onQueryStarted({ caseId, item, openQuantity, holdQuantity }, { dispatch, queryFulfilled }) {
                const result = dispatch(
                    apiSlice.util.updateQueryData('getCase', caseId.toString(), draft => {
                        let existingItem = draft.pick_list[item.category].find(itemData => itemData.id === item.id)
                        if (existingItem) {
                            existingItem.open_quantity = openQuantity
                            existingItem.hold_quantity = holdQuantity
                        }
                    })
                )
                try {
                    await queryFulfilled
                } catch {
                    result.undo()
                }
            }
        }),
        deleteCaseItem: builder.mutation({
            query: ({ caseId, item }) => ({
                url: `/cases/${caseId}/items/${item.id}`,
                method: 'DELETE'
            }),
            async onQueryStarted({ caseId, item }, { dispatch, queryFulfilled }) {
                const result = dispatch(
                    apiSlice.util.updateQueryData('getCase', caseId.toString(), draft => {
                        let existingItem = draft.pick_list[item.category].find(itemData => itemData.id === item.id)
                        if (existingItem) {
                            draft.pick_list[item.category].splice(draft.pick_list[item.category].indexOf(existingItem), 1)
                            if (draft.pick_list[item.category].length === 0) {
                                delete draft.pick_list[item.category];
                            }
                        }
                    })
                )
                try {
                    await queryFulfilled
                } catch {
                    result.undo()
                }
            }
        }),
        updateCardItem: builder.mutation({
            query: ({ cardId, item, openQuantity, holdQuantity }) => ({
                url: `/cards/${cardId}/items/${item.id}`,
                method: 'PATCH',
                body: {
                    open_quantity: openQuantity,
                    hold_quantity: holdQuantity
                }
            }),
            async onQueryStarted({ cardId, item, openQuantity, holdQuantity }, { dispatch, queryFulfilled }) {
                try {
                    const { data: updatedCardItem } = await queryFulfilled;
                    dispatch(
                        apiSlice.util.updateQueryData('getCard', cardId.toString(), card => {
                            let existingItem = card.pick_list[item.category].find(itemData => itemData.id === item.id);
                            if (existingItem) {
                                existingItem.open_quantity = openQuantity;
                                existingItem.hold_quantity = holdQuantity;
                            }
                        })
                    )
                    dispatch(NotificationActions.addSuccessNotification({
                        message: 'Item successfully updated on card.'
                    }));
                } catch {
                    dispatch(NotificationActions.addErrorNotification({
                        message: 'Item could not be updated at this time. Please try again later.'
                    }));
                }
            }
        }),
        deleteCardItem: builder.mutation({
            query: ({ cardId, item }) => ({
                url: `/cards/${cardId}/items/${item.id}`,
                method: 'DELETE'
            }),
            async onQueryStarted({ cardId, item }, { dispatch, queryFulfilled }) {
                try {
                    const { data: updatedCard } = await queryFulfilled; 
                    dispatch(
                        apiSlice.util.updateQueryData('getCard', cardId.toString(), card => {
                            Object.assign(card, updatedCard);
                        })
                    );
                    dispatch(NotificationActions.addSuccessNotification({
                        message: 'Item successfully removed from card.'
                    }));
                } catch {
                    dispatch(NotificationActions.addErrorNotification({
                        message: 'Item could not be removed from card at this time. Please try again later.'
                    }));
                }
            }
        }),
        approveItemImageReview: builder.mutation({
            query: ({ imageId, isManuallyValidated }) => ({
                url: `/itemImages/${imageId}`,
                method: 'PATCH',
                body: {
                    is_manually_validated: isManuallyValidated
                },
            }),
            async onQueryStarted({ imageId, isManuallyValidated }, { dispatch, queryFulfilled }) {
                try {
                    const { data: updatedImage } = await queryFulfilled;
                    dispatch(
                        apiSlice.util.updateQueryData('getItemImageReview', undefined, existingItem => {
                            let existingImage = existingItem.images.find((imageData) => imageData.id === imageId);
                            if (existingImage) {
                                Object.assign(existingImage, updatedImage);
                            }
                        })
                    );
                    dispatch(NotificationActions.addSuccessNotification({
                        message: 'Item image updated successfully'
                    }));
                } catch {
                    dispatch(NotificationActions.addErrorNotification({
                        message: 'Could not update item image at this time. Please try again later.'
                    }));
                }
            }
        }),
        rejectItemImageReview: builder.mutation({
            query: ({ image }) => ({
                url: `/itemImages/${image.id}`,
                method: 'DELETE',
            }),
            async onQueryStarted({ image }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch (
                        apiSlice.util.updateQueryData('getItemImageReview', undefined, existingItem => {
                            let existingImage = existingItem.images.find((imageData) => imageData.id === image.id);
                            if (existingImage) {
                                existingItem.images.splice(existingItem.images.indexOf(existingImage), 1);
                            }
                        })
                    );
                    dispatch(NotificationActions.addSuccessNotification({
                        message: 'Item image deleted successfully'
                    }));
                } catch {
                    dispatch(NotificationActions.addErrorNotification({
                        message: 'Could not remove item image at this time. Please try again later.'
                    }));
                }
            }
        }),
        getItemImagesInfinite: builder.query({
            query: ({ isPackItems = false, page = 1, perPage = 25, columnFilters = [], sorting = [] }) => {
                // Transform sorting IDs for API compatibility - replace periods with underscores
                const transformedSortings = sorting.map(sort => ({
                    ...sort,
                    id: sort.id.replace(/\./g, '_')
                }));

                const filteredString = processFiltersForQuery(columnFilters);
                const sortingString = JSON.stringify(transformedSortings);

                return {
                    url: isPackItems ? '/packItemImages' : '/itemImages',
                    params: {
                        page: page, 
                        perPage,
                        filters: filteredString,
                        sorting: sortingString,
                    }
                };
            },
            serializeQueryArgs: ({ endpointName, queryArgs }) => {
                const cacheKey = {
                    perPage: queryArgs.perPage,
                    columnFilters: queryArgs.columnFilters,
                    sorting: queryArgs.sorting
                };
                return endpointName + JSON.stringify(cacheKey);
            },
            merge: (currentCache, newItems, { arg }) => {
                // Reset data if page is 1 (indicates new sort/filter)
                if (arg.page === 1) {
                    return newItems;
                }
                return {
                    ...newItems,
                    data: [...(currentCache?.data || []), ...newItems.data]
                };
            },
            forceRefetch: ({ currentArg, previousArg }) => {
                return currentArg?.page !== previousArg?.page;
            },
            providesTags: ['ItemImageReview'],
        }),
        getItemImagesInfiniteOrganizationsFilterData: builder.query({
            query: ({ columnFilters = [] } = {}) => {
                const filteredString = processFiltersForQuery(columnFilters);
                return {
                    url: '/itemImages/filterData/organizations',
                    params: { filters: filteredString }
                };
            },
            providesTags: ['ItemImageReview'],
        }),
        getItemImagesInfiniteHospitalsFilterData: builder.query({
            query: ({ columnFilters = [] } = {}) => {
                const filteredString = processFiltersForQuery(columnFilters);
                return {
                    url: '/itemImages/filterData/hospitals',
                    params: { filters: filteredString }
                };
            },
            providesTags: ['ItemImageReview'],
        }),
        getItemImagesInfiniteCampaignsFilterData: builder.query({
            query: ({ columnFilters = [] } = {}) => {
                const filteredString = processFiltersForQuery(columnFilters);
                return {
                    url: '/itemImages/filterData/campaigns',
                    params: { filters: filteredString }
                };
            },
            providesTags: ['ItemImageReview'],
        }),
        getItemImagesInfiniteVendorsFilterData: builder.query({
            query: ({ columnFilters = [] } = {}) => {
                const filteredString = processFiltersForQuery(columnFilters);
                return {
                    url: '/itemImages/filterData/vendors',
                    params: { filters: filteredString }
                };
            },
            providesTags: ['ItemImageReview'],
        }),
        getItemImagesInfiniteCategoriesFilterData: builder.query({
            query: ({ columnFilters = [] } = {}) => {
                const filteredString = processFiltersForQuery(columnFilters);
                return {
                    url: '/itemImages/filterData/categories',
                    params: { filters: filteredString }
                };
            },
            providesTags: ['ItemImageReview'],
        }),
    })    
});

export const { 
    useUpdateCaseItemMutation,
    useDeleteCaseItemMutation,
    useUpdateCardItemMutation,
    useDeleteCardItemMutation,
    useApproveItemImageReviewMutation,
    useRejectItemImageReviewMutation,
    useGetItemImagesInfiniteQuery,
    useGetItemImagesInfiniteOrganizationsFilterDataQuery,
    useGetItemImagesInfiniteHospitalsFilterDataQuery,
    useGetItemImagesInfiniteCampaignsFilterDataQuery,
    useGetItemImagesInfiniteVendorsFilterDataQuery,
    useGetItemImagesInfiniteCategoriesFilterDataQuery,
} = itemSlice;
